import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleCheck, Download, Fingerprint, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import { Banner, Button, Field, Input, PasswordInput, Select, Spinner } from '@/components/ui';
import { useAgentInstall, useInstallAgent, useSshHostKey } from '@/hooks/useNodes';
import { errorReason, errorText } from '@/utils/errors';
import type { EnrollmentIssued, SshHostKeyView } from '@/types/api';

/** Why it did not work, in the operator's words; keyed by the server's `reason`. */
const REASONS: Record<string, string> = {
  unreachable: 'Соединение SSH прервано. Проверьте адрес, порт и доступ с адреса панели.',
  refused: 'На этом порту SSH не отвечает. Проверьте порт.',
  timeout: 'Сервер не отвечает. Проверьте доступ по SSH с адреса панели.',
  auth: 'Неверный логин, пароль или ключ.',
  key_invalid: 'Ключ не распознан. Укажите закрытый ключ целиком и пароль ключа, если он зашифрован.',
  host_key_changed: 'Ключ сервера изменился после проверки — установка остановлена.',
  not_root: 'Нужен root или sudo без пароля.',
  download: 'Сервер не смог скачать установщик. Проверьте доступ с сервера к адресу панели.',
  installer: 'Установщик завершился с ошибкой — его вывод ниже.',
  interrupted:
    'Связь прервалась до завершения установки. Если сервер перейдёт в состояние «Активен», установка завершена.',
};

type Login = 'password' | 'key';

const LOGIN_OPTIONS: { value: Login; label: string }[] = [
  { value: 'password', label: 'Пароль' },
  { value: 'key', label: 'SSH-ключ' },
];

function problem(error: unknown): string {
  const reason = errorReason(error);
  return (reason && REASONS[reason]) ?? errorText(error);
}

/**
 * The panel installs the agent itself (ADR-0036): it logs into the server
 * once, runs the same installer an operator would, and forgets the login. The
 * server's host key is shown first — the login goes only to the key confirmed.
 */
export function SshInstall({
  issued,
  onFallback,
}: {
  readonly issued: EnrollmentIssued;
  /** A fresh token when installing failed before the agent enrolled: the manual way uses it. */
  readonly onFallback: (issued: EnrollmentIssued) => void;
}): ReactNode {
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('root');
  const [login, setLogin] = useState<Login>('password');
  const [password, setPassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [hostKey, setHostKey] = useState<SshHostKeyView | null>(null);
  const [installId, setInstallId] = useState<string | null>(null);

  const probe = useSshHostKey(issued.nodeId, { onSuccess: setHostKey });
  const start = useInstallAgent(issued.nodeId, {
    onSuccess: (view) => {
      // The login has done its one job: nothing of it stays, here either.
      setPassword('');
      setPrivateKey('');
      setPassphrase('');
      setInstallId(view.id);
    },
  });
  const install = useAgentInstall(installId);

  const fallback = install.data?.fallback ?? null;
  useEffect(() => {
    if (fallback) onFallback(fallback);
  }, [fallback, onFallback]);

  const portNumber = Number(port);
  const ready =
    Number.isInteger(portNumber) && portNumber >= 1 && portNumber <= 65_535 && username.trim() !== '' && (login === 'password' ? password !== '' : privateKey.trim() !== '');

  function check(event: FormEvent): void {
    event.preventDefault();
    if (ready) probe.mutate(portNumber);
  }

  function confirm(): void {
    if (!hostKey) return;
    start.mutate({
      port: portNumber,
      username: username.trim(),
      fingerprint: hostKey.fingerprint,
      ...(login === 'password' ? { password } : { privateKey, ...(passphrase ? { passphrase } : {}) }),
    });
  }

  if (installId) {
    return (
      <Progress
        view={install.data}
        error={install.error}
        hostKey={hostKey}
        onRetry={() => {
          start.reset();
          probe.reset();
          setHostKey(null);
          setInstallId(null);
        }}
      />
    );
  }

  if (hostKey) {
    return (
      <div className="stack">
        <Banner tone="info" icon={Fingerprint}>
          <div className="stack stack-tight">
            <span>
              Сервер <span className="mono">{hostKey.address}:{hostKey.port}</span> представился ключом {hostKey.algorithm}:
            </span>
            <span className="mono" style={{ wordBreak: 'break-all' }}>
              {hostKey.fingerprint}
            </span>
          </div>
        </Banner>
        <p className="field-hint" style={{ margin: 0 }}>
          Сверьте отпечаток на сервере: <span className="mono">ssh-keygen -lf /etc/ssh/ssh_host_{hostKey.algorithm.replace(/^ssh-|-sha2-nistp\d+$/g, '')}_key.pub</span>
        </p>
        {start.isError ? (
          <p className="field-error" role="alert">
            {problem(start.error)}
          </p>
        ) : null}
        <div className="row-tight">
          <Button variant="primary" icon={Download} busy={start.isPending} onClick={confirm}>
            Всё верно, установить
          </Button>
          <Button
            variant="ghost"
            disabled={start.isPending}
            onClick={() => {
              start.reset();
              setHostKey(null);
            }}
          >
            Назад
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="stack" onSubmit={check} noValidate>
      <p className="field-hint" style={{ margin: 0 }}>
        Данные для входа используются один раз и не сохраняются.
      </p>
      <div className="row-tight" style={{ alignItems: 'flex-start' }}>
        <Field label="Пользователь" htmlFor="ssh-user" hint="root или с sudo без пароля">
          <Input id="ssh-user" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="off" />
        </Field>
        <Field label="Порт SSH" htmlFor="ssh-port">
          <Input id="ssh-port" value={port} onChange={(event) => setPort(event.target.value)} inputMode="numeric" style={{ maxWidth: 110 }} />
        </Field>
        <Field label="Вход" htmlFor="ssh-login">
          <Select id="ssh-login" label="Вход" value={login} onChange={setLogin} options={LOGIN_OPTIONS} />
        </Field>
      </div>
      {login === 'password' ? (
        <Field label="Пароль" htmlFor="ssh-password">
          <PasswordInput id="ssh-password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="off" />
        </Field>
      ) : (
        <>
          <Field label="Закрытый ключ" htmlFor="ssh-key" hint="Содержимое файла ключа целиком, например ~/.ssh/id_ed25519">
            <textarea
              id="ssh-key"
              className="input mono"
              rows={5}
              value={privateKey}
              onChange={(event) => setPrivateKey(event.target.value)}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
              spellCheck={false}
            />
          </Field>
          <Field label="Пароль ключа" htmlFor="ssh-passphrase" hint="Если ключ зашифрован">
            <PasswordInput id="ssh-passphrase" value={passphrase} onChange={(event) => setPassphrase(event.target.value)} autoComplete="off" />
          </Field>
        </>
      )}
      {probe.isError ? (
        <p className="field-error" role="alert">
          {problem(probe.error)}
        </p>
      ) : null}
      <div>
        <Button type="submit" variant="primary" icon={Download} busy={probe.isPending} disabled={!ready}>
          Установить агента
        </Button>
      </div>
    </form>
  );
}

function Progress({
  view,
  error,
  hostKey,
  onRetry,
}: {
  readonly view: ReturnType<typeof useAgentInstall>['data'];
  readonly error: unknown;
  readonly hostKey: SshHostKeyView | null;
  /** Back to the form: the login was not kept, so it is asked again. */
  readonly onRetry: () => void;
}): ReactNode {
  const log = useRef<HTMLPreElement | null>(null);
  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [view?.log.length]);

  if (!view) return error ? <p className="field-error">{errorText(error)}</p> : <Spinner label="Запускаем установку…" />;

  return (
    <div className="stack">
      {view.state === 'running' ? (
        <div className="row-tight">
          <Loader2 size={16} className="spin" aria-hidden="true" />
          <span>Установка агента{hostKey ? ` на ${hostKey.address}` : ''}… Обычно 1–3 минуты.</span>
        </div>
      ) : view.state === 'succeeded' ? (
        <Banner tone="info" icon={CircleCheck}>
          <span className="tone-healthy">Агент установлен и подключён.</span>
        </Banner>
      ) : (
        <Banner tone="danger" icon={TriangleAlert}>
          <div className="stack stack-tight">
            <span>{(view.reason && REASONS[view.reason]) ?? 'Установка не удалась.'}</span>
            {view.fallback ? <span>Завершите установку командой на вкладке «Команда вручную».</span> : null}
          </div>
        </Banner>
      )}
      {view.log.length > 0 ? (
        <pre ref={log} className="code-block" style={{ maxHeight: 280, overflow: 'auto', margin: 0 }}>
          {view.log.join('\n')}
        </pre>
      ) : null}
      {view.state === 'failed' ? (
        <div>
          <Button icon={RotateCcw} onClick={onRetry}>
            Попробовать ещё раз
          </Button>
        </div>
      ) : null}
    </div>
  );
}
